import { useCallback, useEffect, useMemo, useState } from 'react'

import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { useMutation, useQueryClient } from 'react-query'
import { Link, useNavigate } from 'react-router-dom'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import {
    IconActionDollar,
    IconCaretDownSm,
    IconCaretUpSm,
    IconChatOutline,
    IconTwoPeopleOutline
} from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import SubscribeProfileModal from 'components/UI/Modal/profileModals/SubscribeProfileModal/SubscribeProfileModal'
import TipModal from 'components/UI/Modal/Tip/TipModal'
import { Checkbox } from 'components-ref/atoms'
import { useModalContext } from 'context/modalContext'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import { AllIcons } from 'helpers/allIcons'
import './_creator.scss'
import { toFixedNumber } from 'helpers/dataTransformations'
import { useSubscribeMutation } from 'helpers/hooks/subscription/useSubscribeMutation'
import { useSubscriptionPlans } from 'helpers/hooks/subscription/useSubscriptionPlans'
import { useRefetchUserProfile } from 'helpers/hooks/useRefetchUserProfile/useRefetchUserProfile'
import { localeDate, localeShortWordDate, wordedDayDate } from 'lib/dayjs'
import { subscribeToUser } from 'services/endpoints/profile'
// import { getSubscriptionPlans } from 'services/endpoints/settings/general/subscription'
import { creatorsImpression } from 'services/endpoints/tracking'
import { useUserProfile } from 'store'
import { type SubscribePayload } from 'types/api/profile'
// import { type PaymentPlan, type PromoCampaign } from 'types/api/subscription'
// import { PromoCampaignType } from 'types/enums'
import { type ICreator } from 'types/interfaces/ICreator'
import { OptionsDropdown } from 'components-ref/molecules'

interface Props {
    creatorData: ICreator
    renderLocation: string
    selected?: boolean
    onSelect?: () => void
    listSelection?: boolean
    tab?: string,
    refetchSubscriptions?: any
    onCreatorImpression?: (id?: string) => void
    onRemoveFromList?: () => void | Promise<void>
}

type CreatorExpiredProps = {
  creatorData: any
  setSubscribeModalOpen?: any // you can remove this prop now if you want
}

export const CreatorExpired = ({ creatorData }: CreatorExpiredProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userData = useUserProfile()

  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)

  const otherUserId = useMemo(() => {
    const id = creatorData?.userId ?? creatorData?.user_id ?? creatorData?.id
    return typeof id === 'string' ? id : ''
  }, [creatorData])

    const subscriptionPlans = creatorData?.subscriptionPlans ?? []
    const promoCampaign = creatorData?.promoCampaign ?? null

  const blockedUserIds = useMemo(
    () => (userData?.blocke_by_model ?? []).map((u: any) => u.id),
    [userData]
  )

  const isBlocked = useMemo(() => {
    if (!otherUserId) return false
    return blockedUserIds.includes(otherUserId)
  }, [blockedUserIds, otherUserId])

  const coverStyle = useMemo(
    () => ({
      backgroundImage: creatorData.coverUrl
        ? `url(${creatorData.coverUrl})`
        : 'linear-gradient(135deg, #00cfd1, #09192d 40%, #8a003c, #ff003c)',
    }),
    [creatorData.coverUrl]
  )

  // We already have the plans from expiredSubscriptions, so don't fetch them again.
  const shouldLoadSubscriptionPlans =
    subscribeModalOpen &&
    !!otherUserId &&
    !isBlocked &&
    subscriptionPlans.length === 0

  const {
    selectedSubscriptionPlan,
    handleSetSelectedSubscriptionPlan,
    queryKey,
  } = useSubscriptionPlans({
    otherUserId,
    shouldLoadSubscriptionPlans,
  })

  const { subscribeMutation } = useSubscribeMutation({
    otherUserId,
    selectedSubscriptionPlan,
    t,
    onClose: () => setSubscribeModalOpen(false),
    invalidateKey: queryKey,
    onSubscribed: () => setIsSubscribed(true),
  })

  return (
    <>
      <div className='creator'>
        {!!creatorData.save && (
          <div className='creator__discount'>
            {t('save')} {creatorData.save}%
          </div>
        )}

        <div
          className='creator__wrapper creator__wrapper--expired'
          style={coverStyle}
        >
          <div className='creator__expired--bg' />

          <div
            onClick={() => {
              if (isBlocked) {
                addToast('error', 'Creator has blocked you.')
                return
              }

              navigate(`/${creatorData.handle}`)
            }}
            className={`creator__avatar ${
              creatorData.isOnline ? 'active' : ''
            }`}
          >
            <img
              src={creatorData.avatarUrl || placeholderAvatar}
              alt={creatorData.name || creatorData.handle}
            />

            {creatorData.isLive && (
              <div className='creator__live'>
                <span>{t('live')}</span>
              </div>
            )}
          </div>

          <div className='creator__expired'>
            <div className='creator__expired__text'>
              <p className='creator__expired__text--name creator__text--name'>
                {creatorData.name}
              </p>

              <p className='creator__expired__text--handle creator__text--handle'>
                @{creatorData.handle}
              </p>

              <p className='creator__expired__text--date creator__text--expired'>
                <span>{t('expired')}</span>{' '}
                {localeShortWordDate(creatorData.expires)}
              </p>
            </div>

            <div className='creator__expired__actions'>
              <div className='creator__expired__actions--bg' />

              <div className='creator__expired__actions--price'>
                ${toFixedNumber(creatorData.price)}
                <span>&nbsp;/&nbsp;{t('monthShort')}</span>
              </div>

              <div className='creator__expired__actions--button'>
                <Button
                  text={isSubscribed ? t('subscribed') : t('subscribe')}
                  color={isSubscribed ? 'grey' : 'blue'}
                  width='11'
                  height='3'
                  padding='2'
                  font='mont-14-normal'
                  clickFn={() => {
                    if (isBlocked) {
                      addToast('error', 'Creator has blocked you.')
                      return
                    }

                    if (isSubscribed) return

                    setSubscribeModalOpen(true)
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {subscribeModalOpen && (
        <ModalWrapper
          open={subscribeModalOpen}
          setOpen={setSubscribeModalOpen}
          customClass='subscribe-profile-modal-modal'
          hasCloseButton
        >
          <SubscribeProfileModal
            displayName={creatorData?.name}
            avatar={creatorData?.avatarUrl}
            username={creatorData?.handle}
            id={otherUserId}
            selected={selectedSubscriptionPlan}
            setSelected={handleSetSelectedSubscriptionPlan}
            initialPlans={subscriptionPlans}
            initialCampaign={promoCampaign}
            subscribeFn={(paymentMethod: {
              method: 'card' | 'deposit'
              cardId: string | undefined
              rebill?: boolean
              taxRate?: any
              taxRateFee?: any
            }) => {
              subscribeMutation.mutate(paymentMethod)
            }}
            isLoading={subscribeMutation.isLoading}
          />
        </ModalWrapper>
      )}
    </>
  )
}
    type CreatorReferralProps = {
        creatorData: ICreator
        statsExpanded: boolean
        addTip: () => void
        toggleExpanded: () => void
    }

    const CreatorReferral = ({ creatorData, addTip }: CreatorReferralProps) => {
        const { t } = useTranslation()
        const navigate = useNavigate()

        const coverStyle = {
            backgroundImage: creatorData.coverUrl
                ? `url(${creatorData.coverUrl})`
                : 'linear-gradient(135deg, #00cfd1, #09192d 40%, #8a003c, #ff003c)'
        }

    return (
        <div className={'creator statsExpanded'}>
            <div
                className={'creator__wrapper'}
                style={coverStyle}
            >
                <div
                    className={`creator__avatar ${
                        creatorData.isOnline ? 'active' : ''
                    }`}
                >
                    <img src={creatorData.avatarUrl} alt='' />
                    {creatorData.isLive && (
                        <div className='creator__live'>
                            <span>{t('live')}</span>
                        </div>
                    )}
                </div>
                <div className='creator__text'>
                    <div className='creator__text--bg'></div>
                    <div className='creator__text--wrapper'>
                        <div className='creator__text--name'>
                            {creatorData.name}
                        </div>
                        <div className='creator__text--handle'>
                            @{creatorData.handle}
                        </div>
                    </div>
                    <div className='creator__actions'>
                        <div
                            className='creator__actions__button'
                            onClick={addTip}
                        >
                            <IconTwoPeopleOutline color='#ffffff' />
                        </div>
                        <div
                            className='creator__actions__button creator__actions__button--message'
                            onClick={() => {
                                navigate(`/chat/${creatorData.userId}`)
                            }}
                        >
                            <IconChatOutline />
                        </div>
                    </div>
                </div>
            </div>
            <div className={'creator__stats creator__stats__referrals '}>
                <div className='creator__stat'>
                    <div className='creator__stat__name'>
                        {t('DateRegistered')}
                    </div>
                    <div className='creator__stat__value'>
                        {dayjs(creatorData.registeredAt).format('ll')}
                    </div>
                </div>
                <div className='creator__stat'>
                    <div className='creator__stat__name'>{t('earnings')}</div>
                    <div className='creator__stat__value'>
                        <span>${toFixedNumber(creatorData.price, 2)}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

type CreatorActiveProps = {
    creatorData: any
    statsExpanded: boolean
    addTip: () => void
    toggleExpanded: () => void
    listSelection?: boolean
    selected?: boolean
    onSelect?: () => void
    onRemoveFromList?: () => void | Promise<void>
}

const CreatorActive = ({
    creatorData,
    statsExpanded,
    addTip,
    toggleExpanded,
    listSelection = false,
    selected = false,
    onSelect,
    onRemoveFromList
}: CreatorActiveProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const expiresSoon = (date: string) => {
    const currentDate = new Date()
    const navigate = useNavigate()
    const afterSevenDays = new Date()
    const dataDate = new Date(date)
        afterSevenDays.setDate(currentDate.getDate() + 7)
        return dataDate >= currentDate && dataDate <= afterSevenDays
    }
const toChatUserShape = (c: any) => ({
  id: String(c.userId),
    username: c.handle,
    display_name: c.name,
    avatar: {
        id: null,
        url: c.avatarUrl ?? null,
        created_at: null,
    },
    conversation_id: c.conversation_id ?? null,
    isSubscribed: !!c.isActive,
    is_model: true,
    name: c.name,
    })

    const expiresDate = expiresSoon(creatorData.expires)
    const isRenewing = !!creatorData.renewDate

    const coverStyle = {
        backgroundImage: creatorData.coverUrl
            ? `url(${creatorData.coverUrl})`
            : 'linear-gradient(135deg, #00cfd1, #09192d 40%, #8a003c, #ff003c)'
    }

    return (
        <div className={`creator ${statsExpanded ? 'statsExpanded' : ''}`}>
            <Link
                to={`/${creatorData.handle}`}
                className={'creator__wrapper'}
                style={coverStyle}
            >
            <div
            className={`creator__avatar ${
                creatorData.isOnline ? 'active' : ''
            } ${creatorData.isLive ? 'creator__avatar--live' : ''}`}
                onClick={e => {
                    if (!creatorData.isLive) return

                    e.preventDefault()
                    e.stopPropagation()
                    navigate(`/watch/${creatorData.handle}`)
                }}
            >
                <img src={creatorData.avatarUrl || placeholderAvatar} alt='' />
                {creatorData.isLive && (
                    <div className='creator__live'>
                        <span>{t('live')}</span>
                    </div>
                )}
            </div>

                {(expiresDate || creatorData.newPostsCount > 0) && (
                    <div className='creator__topinfo'>
                        {creatorData.newPostsCount > 0 && (
                            <div className='creator__topinfo__item'>
                                <div className='creator__topinfo__item__bg creator__topinfo__item__bg--blue'></div>
                                <p>
                                    <span>{creatorData.newPostsCount}</span>
                                    {t('newPosts')}
                                </p>
                            </div>
                        )}
                        {!creatorData.renewDate && (
                            <div className='creator__topinfo__item'>
                                <div className='creator__topinfo__item__bg creator__topinfo__item__bg--trans'></div>
                                <p>
                                    {t('expires')}{' '}
                                    {localeDate(creatorData.expires)}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <div className='creator__text'>
                    <div className='creator__text--bg'></div>
                    <div className='creator__text--wrapper'>
                        <div className='creator__text--name'>
                            {creatorData.name}
                        </div>
                        <div className='creator__text--handle'>
                            @{creatorData.handle}
                        </div>
                    </div>

                    {/* Action buttons must not trigger profile navigation */}
                    <div
                        className='creator__actions'
                        onClick={e => e.preventDefault()} // prevent link navigation
                    >
                        <button
                            className='creator__actions__button'
                            onClick={e => {
                                e.stopPropagation()
                                e.preventDefault()
                                addTip()
                            }}
                        >
                            <IconActionDollar />
                        </button>
                        <button
                        className="creator__actions__button creator__actions__button--message"
                        onClick={e => {
                            e.stopPropagation()
                            e.preventDefault()

                            const otherUserData = toChatUserShape(creatorData)

                            const convId =
                            typeof otherUserData.conversation_id === 'string' &&
                            otherUserData.conversation_id
                                ? otherUserData.conversation_id
                                : null

                            navigate(`/chat/${otherUserData.id}`, {
                            state: {
                                otherUserData,
                                ...(convId ? { conversationId: convId } : {}),
                            },
                            })
                        }}
                        >
                        <IconChatOutline />
                        </button>
                        {!listSelection && (
                            <button
                                type="button"
                                className="creator__actions__button creator__actions__button--message"
                                onClick={e => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    toggleExpanded()
                                }}
                            >
                                {statsExpanded ? <IconCaretUpSm /> : <IconCaretDownSm />}
                            </button>
                        )}
                        {listSelection && selected && (
                            <OptionsDropdown
                                role="owner"
                                content="listCreator"
                                isOptionsNavOpen={selected}
                                setIsOptionsNavOpen={(open: boolean) => {
                                    if (!open) {
                                        onSelect?.()
                                    }
                                }}
                                postId={creatorData.userId}
                                options={[]}
                                deleteFn={() => {
                                    void onRemoveFromList?.()
                                }}
                            />
                        )}
                    </div>
                </div>
            </Link>
            {listSelection && (
                <button
                    type="button"
                    className={`creator__list-select ${
                        selected ? 'creator__list-select--selected' : ''
                    }`}
                    onClick={onSelect}
                    aria-label={
                        selected
                            ? `Deselect ${creatorData.name}`
                            : `Select ${creatorData.name}`
                    }
                    aria-pressed={selected}
                >
                    <span className="creator__list-select__circle">
                        <span className="creator__list-select__dot" />
                    </span>
                </button>
            )}
            <div className={'creator__stats'}>
                <div className='creator__stat'>
                    <div className='creator__stat__name'>{t('status')}</div>
                    <div
                        className={`creator__stat__value creator__stat__value--status creator__stat__value--${
                            creatorData.isActive ? 'active' : 'expired'
                        }`}
                    >
                        {creatorData.isActive ? t('active') : t('expired')}
                    </div>
                </div>
                <div className='creator__stat'>
                    <div className='creator__stat__name'>{t('billingCicle')}</div>
                    <div className='creator__stat__value'>
                        {creatorData.billingCycle}{' '}
                        {creatorData.billingCycle === 1 ? 'Month' : 'Months'}
                    </div>
                </div>
                <div className='creator__stat'>
                    <div className='creator__stat__name'>{t('nextBillingCycle')}</div>
                    <div className='creator__stat__value'>
                        {'1 Month'}
                    </div>
                </div>
                <div className='creator__stat'>
                    <div className='creator__stat__name'>
                        {t('totalMonthSubscribed')}
                    </div>
                    <div className='creator__stat__value'>
                        {creatorData.monthsSubscribed}{' '}
                        {creatorData.monthsSubscribed === 1 ? 'Month' : 'Months'}
                    </div>
                </div>
                <div className='creator__stat'>
                    <div className='creator__stat__name'>
                        {t('subscriptionPrice')}
                    </div>
                    <div className='creator__stat__value'>
                        <span>${toFixedNumber(creatorData.current_plan_price)}</span>{' '}
                        {t('willRenewAt')}{' '}
                        <span>${toFixedNumber(creatorData.renewalPrice)}</span>
                    </div>
                </div>
                <div className='creator__stat'>
                <div className='creator__stat__name'>
                    {isRenewing ? t('subRenewDate') : t('expires')}
                </div>
                <div className='creator__stat__value'>
                    {isRenewing
                    ? wordedDayDate(creatorData.renewDate)
                    : wordedDayDate(creatorData.expires)}
                </div>
                </div>
            </div>
        </div>
    )

}

type CreatorDefaultProps = {
    creatorData: ICreator
    withSubscribed?: boolean
    tab?: string
}



const CreatorDefault = ({
    creatorData,
    withSubscribed = false,
    tab
}: CreatorDefaultProps) => {
    const { t } = useTranslation()
    const coverStyle = {
        backgroundImage: creatorData.coverUrl
            ? `url(${creatorData.coverUrl})`
            : 'linear-gradient(135deg, #00cfd1, #09192d 40%, #8a003c, #ff003c)'
    }
    return (
        <Link
            to={`/${creatorData.handle}`}
            state={{ tabName: tab }}
            className={'creator'}
        >
            <div
                className={'creator__wrapper'}
                style={coverStyle}
            >
                <div
                    className={`creator__avatar ${
                        creatorData.isOnline ? 'active' : ''
                    }`}
                >
                    <img src={creatorData.avatarUrl || placeholderAvatar} alt='' />
                    {creatorData.isLive && (
                        <div className='creator__live'>
                            <span>{t('live')}</span>
                        </div>
                    )}
                </div>
                <div className='creator__text'>
                    <div className='creator__text--bg'></div>
                    <div className='creator__text--wrapper'>
                        <div className='creator__text--name'>
                            {creatorData.name}
                        </div>
                        <div className='creator__text--handle'>
                            @{creatorData.handle}
                        </div>
                    </div>
                    {withSubscribed && creatorData.subscribed ? (
                        <div className='creator__text--subscribed'>
                            <img
                                src={AllIcons.profile_checkcircle}
                                alt='checked circle'
                            />
                            <span>{t('subscribed')}</span>
                        </div>
                    ) : null}
                </div>
            </div>
        </Link>
    )
}

type CreatorSelectProps = {
  creatorData: ICreator
  selected: boolean
  clickFn?: () => void
}

const CreatorSelect = ({ creatorData, selected, clickFn }: CreatorSelectProps) => {
  const { t } = useTranslation()
  const coverStyle: React.CSSProperties = {
    backgroundImage: creatorData.coverUrl
      ? `url(${creatorData.coverUrl})`
      : 'linear-gradient(135deg, #00cfd1, #09192d 40%, #8a003c, #ff003c)',
  }

  return (
    <div className="creator">
      <div
        className="creator__wrapper creator__wrapper--select"
        style={coverStyle}
      >
        <div className={`creator__avatar ${creatorData.isOnline ? 'active' : ''}`}>
          <img
            src={creatorData.avatarUrl || placeholderAvatar}
            alt={creatorData.name || creatorData.handle || ''}
          />
          {creatorData.isLive && (
            <div className="creator__live">
              <span>{t('live')}</span>
            </div>
          )}
        </div>

        <div className="creator__text">
          <div className="creator__text--bg" />
          <div className="creator__text--wrapper">
            <div className="creator__text--name">{creatorData.name}</div>
            <div className="creator__text--handle">@{creatorData.handle}</div>
          </div>
        </div>

        <div className="creator__select">
          <Checkbox
            id={`subscribers-select-subscriber-${creatorData.userId}`}
            value="select"
            label=""
            checked={selected}
            handleOnChange={() => clickFn?.()}
          />
        </div>
      </div>
    </div>
  )
}

const Creator = ({
    creatorData,
    renderLocation,
    selected,
    onSelect,
    onRemoveFromList,
    listSelection = false,
    tab,
    refetchSubscriptions
}: Props) => {
    const [statsExpanded, setStatsExpanded] = useState(false)
    const modalData = useModalContext()
    const userData = useUserProfile()
    const { t } = useTranslation()
    const { refetchUserProfile } = useRefetchUserProfile()
    const [selectedSubscriptionPlan, setSelectedSubscriptionPLan] =
    useState<string>('')

    const [campaign, setCampaign] = useState<string | null>(null)
    const [subscribeModalOpen, setSubscribeModalOpen] = useState(false)

    const queryClient = useQueryClient()

    const addTip = () => {
        if (userData?.status === 'restricted' && userData?.role === 'fan') {
            addToast('error', 'Your account is restricted.')
            return
        }

        modalData.addModal(
            t('tipAmount'),
            <TipModal
                avatar={creatorData.avatarUrl}
                modelData={{
                    modelId: creatorData.userId,
                    avatarSrc: creatorData.avatarUrl
                }}
            />,
            false,
            false,
            'tip-modal-overlay',
            '',
            true // keyboardAdjust
        )
    }

    const handleSetSelectedSubscriptionPlan = useCallback((planId: any) => {
        setSelectedSubscriptionPLan(planId)
    }, [])

    const subscribeToModel = async (paymentMethod: {
        method: 'card' | 'deposit'
        cardId: string | undefined
        rebill?: boolean
        taxRate?: any
        taxRateFee?: any
    }) => {
        const subscribePayload: SubscribePayload = {
            id: creatorData?.userId,
            subscription_plan_id:
                selectedSubscriptionPlan.replace('campaign', ''),
            promo_campaign_id: campaign,
            payment_method: paymentMethod.method,
            suggested: tab,
            card_id: paymentMethod.cardId,
            rebill: paymentMethod?.rebill,
            tax: paymentMethod?.taxRate,
            tax_rate: paymentMethod?.taxRateFee
        }

        return await subscribeToUser(subscribePayload)
    }

    const subscribeMutation = useMutation(
        async (paymentMethod: {
            method: 'card' | 'deposit'
            cardId: string | undefined
            rebill?: boolean
            taxRate?: any
            taxRateFee?: any
        }) => await subscribeToModel(paymentMethod),
        {
            onSuccess: resp => {
                refetchSubscriptions?.()
                refetchUserProfile()
                queryClient.invalidateQueries(['getFeed', creatorData?.userId])
                setSubscribeModalOpen(false)

                queryClient.invalidateQueries([
                    'allSubsPlans_' + creatorData?.userId,
                    creatorData?.userId
                ])

                if (resp.error) {
                    addToast('error', resp.error)
                    setSubscribeModalOpen(false)
                }
            },
            onError: () => {
                refetchSubscriptions?.()
                setSubscribeModalOpen(false)
                addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
            }
        }
    )

    switch (renderLocation) {
        case 'subscriptionsExpired':
            return (
                <>
                    <CreatorExpired
                        creatorData={creatorData}
                        setSubscribeModalOpen={setSubscribeModalOpen}
                    />

                    <ModalWrapper
                        open={subscribeModalOpen}
                        setOpen={setSubscribeModalOpen}
                        customClass='subscribe-profile-modal-modal'
                        hasCloseButton={true}
                    >
                        <SubscribeProfileModal
                            displayName={creatorData?.name}
                            avatar={creatorData?.avatarUrl}
                            username={creatorData?.handle}
                            id={creatorData?.userId}
                            subscribeFn={(paymentMethod: {
                                method: 'card' | 'deposit'
                                cardId: string | undefined
                                rebill?: boolean
                                taxRate?: any
                                taxRateFee?: any
                            }) => {
                                subscribeMutation.mutate(paymentMethod)
                            }}
                            isLoading={subscribeMutation.isLoading}
                            selected={selectedSubscriptionPlan}
                            setSelected={handleSetSelectedSubscriptionPlan}
                            campaign={campaign}
                            setCampaign={setCampaign}
                        />
                    </ModalWrapper>
                </>
            )

        case 'subscriptionsActive':
            return (
                <CreatorActive
                    creatorData={creatorData}
                    statsExpanded={statsExpanded}
                    addTip={addTip}
                    toggleExpanded={() => {
                        setStatsExpanded(statsExpanded => !statsExpanded)
                    }}
                    listSelection={listSelection}
                    selected={selected}
                    onSelect={onSelect}
                    onRemoveFromList={onRemoveFromList}
                />
            )
        case 'referrals':
            return (
                <CreatorReferral
                    creatorData={creatorData}
                    statsExpanded={statsExpanded}
                    addTip={addTip}
                    toggleExpanded={() => {
                        setStatsExpanded(statsExpanded => !statsExpanded)
                    }}
                />
            )

        case 'creators':
            return <CreatorDefault creatorData={creatorData} tab={tab} />

        case 'suggested':
            return <CreatorDefault creatorData={creatorData} />

        case 'publicFriends':
            return (
                <CreatorDefault
                    creatorData={creatorData}
                    withSubscribed={true}
                />
            )

        case 'newList':
            return (
                <CreatorSelect
                    creatorData={creatorData}
                    selected={!!selected}
                    clickFn={onSelect}
                />
            )

        default:
            return <CreatorDefault creatorData={creatorData} />
    }
}

export default Creator